import DutyStatusLog from "../../../model/DutyStatusLog";
import type { THosStatus } from "hos-tools";
import type { DutyStatusLogService } from "../../../model/DutyStatusLogs";

/**
 * Checks if there's an engine cycle sequence (EngineShutdownPC -> EnginePowerupPC)
 * that brackets the latest log, indicating the driver is continuing PC.
 */
export const isDriverContinuingPC = (
    latestLog: DutyStatusLog,
    userId: string,
    dutyStatusLogService: DutyStatusLogService
): boolean => {
    const engineLogs = dutyStatusLogService.getHOSLogs(true, userId, false, true)
        .filter(fetchedLog => fetchedLog.isEngineLog());

    if (engineLogs.length >= 2) {
        const lastEngineLog = engineLogs[engineLogs.length - 1];
        const secondLastEngineLog = engineLogs[engineLogs.length - 2];
        const logDate = latestLog.getDate();
        const isCorrectSequence = lastEngineLog.status === "EnginePowerupPC" &&
            secondLastEngineLog.status === "EngineShutdownPC";

        if (isCorrectSequence &&
            lastEngineLog.getDate() > logDate &&
            secondLastEngineLog.getDate() < logDate) {
            return true;
        }
    }

    return false;
};

/**
 * Checks if the PC period being ended is PC_Exempted. The ended-PC prompt does not apply to
 * exempt PC: the status continues across ignition cycles until the driver explicitly ends it.
 */
export const isPcExemptedPeriod = (
    logBeforePromptLog: DutyStatusLog | null,
    collection: DutyStatusLog[],
    dutyStatusLogService: DutyStatusLogService
): boolean => {
    if (logBeforePromptLog?.isPCLog()) {
        return dutyStatusLogService.getLatestPcLog(collection)?.status === "PC_Exempted";
    }
    return false;
};

/**
 * Returns true if the prompt shouldn't be shown because the previous log
 * doesn't match the expected status, or because the driver is continuing PC.
 */
export const isThePromptNotExpected = (
    logBeforePromptLog: DutyStatusLog | null,
    newStatus: THosStatus,
    slimLogDriverId: string,
    isEndedPcPrompt: boolean,
    dutyStatusLogService: DutyStatusLogService
): boolean => {
    if (!logBeforePromptLog || (logBeforePromptLog.status !== newStatus && !(newStatus === "PC" && logBeforePromptLog.isPCLog()))) {
        return true;
    }

    return isEndedPcPrompt && isDriverContinuingPC(logBeforePromptLog, slimLogDriverId, dutyStatusLogService);
};
